import {
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
  useRef,
  useState,
  startTransition,
} from "react";

import { Excalidraw, serializeAsJSON } from "@excalidraw/excalidraw";
import { newElementWith } from "@excalidraw/element";

import type {
  AppState,
  BinaryFiles,
  ExcalidrawProps,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  createHybridShareLink,
  deleteHybridItem,
  enableCollaborationRoom,
  getHybridItem,
  revokeShareLink,
  updateHybridItemMeta,
  updateItemContent,
  buildPublicShareUrl,
} from "./api";
import { LibraryIcon } from "@excalidraw/excalidraw/components/icons";

import { AIComponents } from "../components/AI";
import { AppSidebar } from "../components/AppSidebar";
import { type FrameRef } from "./FrameMention";

import { parseDrawingContent } from "./content";
import { colorForUser, userHandle } from "./identity";
import { HybridMarkdownPane } from "./HybridMarkdownPane";
import { KindrawIcon } from "./icons";
import { PresenceFacepile } from "./PresenceFacepile";
import { useCanvasCollab } from "./useCanvasCollab";
import {
  buildHybridPath,
  buildItemPath,
  buildFolderPath,
  navigateKindraw,
} from "./router";
import { ShareHybridModal } from "./ShareHybridModal";
import { getKindrawDraft, setKindrawDraft } from "./storage";
import { usePresence } from "./usePresence";
import { getErrorMessage, isDraftNewer } from "./utils";
import {
  appendHybridSection,
  buildKindrawSectionLink,
  parseHybridMarkdownSections,
  parseKindrawSectionLink,
} from "./hybridSections";

import { KindrawYjsProvider } from "./yjsProvider";
import { useKindrawI18n } from "./i18n";

import type { TranslationKeys } from "@excalidraw/excalidraw/i18n";

import type { PointerEvent as ReactPointerEvent } from "react";
import type {
  KindrawFolder,
  KindrawHybridItemResponse,
  KindrawHybridView,
  KindrawItem,
  KindrawUser,
} from "./types";

type HybridEditorPageProps = {
  hybridId: string;
  initialView: KindrawHybridView;
  initialSectionId: string | null;
  itemsById: Record<string, KindrawItem>;
  onTreeRefresh: () => Promise<void> | void;
  folders?: KindrawFolder[];
  currentUser?: KindrawUser | null;
};

// Sentinela usado para dividir a instrução da linkbar em torno do título da
// seção, permitindo renderizar o <strong>{title}</strong> como componente no
// meio da string traduzida (interpolação com componente, não só texto).
const LINKBAR_TITLE_SENTINEL = " __title__ ";

const getHybridViewLabels = (
  t: (key: TranslationKeys) => string,
): { view: KindrawHybridView; label: string }[] => [
  { view: "document", label: t("kindraw.hybrid.viewDocument") },
  { view: "both", label: t("kindraw.hybrid.viewBoth") },
  { view: "canvas", label: t("kindraw.actions.currentCanvas") },
];

const useNarrowLayout = () => {
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window === "undefined" ? false : window.innerWidth < 1100,
  );

  useEffect(() => {
    const onResize = () => {
      setIsNarrow(window.innerWidth < 1100);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isNarrow;
};

const HYBRID_SPLIT_STORAGE_PREFIX = "kindraw:hybrid-split:";
const DEFAULT_SPLIT_RATIO = 0.42;
const clampSplitRatio = (value: number) =>
  Math.min(0.68, Math.max(0.32, value));

export const HybridEditorPage = ({
  hybridId,
  initialView,
  initialSectionId,
  itemsById,
  onTreeRefresh,
  folders,
  currentUser,
}: HybridEditorPageProps) => {
  const { t } = useKindrawI18n();
  const [response, setResponse] = useState<KindrawHybridItemResponse | null>(
    null,
  );
  const [title, setTitle] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [lastSavedMarkdown, setLastSavedMarkdown] = useState("");
  const [drawingSeedContent, setDrawingSeedContent] = useState<string | null>(
    null,
  );
  const [serializedDrawing, setSerializedDrawing] = useState("");
  const [lastSavedDrawing, setLastSavedDrawing] = useState("");
  const [statusMessage, setStatusMessage] = useState(() =>
    t("kindraw.hybrid.statusLoading"),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "error">(
    "idle",
  );
  const [mobilePane, setMobilePane] = useState<"document" | "canvas">(
    "document",
  );
  const [activeSectionId, setActiveSectionId] = useState<string | null>(
    initialSectionId,
  );
  const [linkingSectionId, setLinkingSectionId] = useState<string | null>(null);
  const [selectedElementIds, setSelectedElementIds] = useState<
    Record<string, true>
  >({});
  const [sceneElements, setSceneElements] = useState<
    readonly ExcalidrawElement[]
  >([]);
  const [sceneAppState, setSceneAppState] = useState<AppState | null>(null);
  const [sceneFiles, setSceneFiles] = useState<BinaryFiles>({});
  const [splitRatio, setSplitRatio] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_SPLIT_RATIO;
    }

    const storedValue = window.localStorage.getItem(
      `${HYBRID_SPLIT_STORAGE_PREFIX}${hybridId}`,
    );
    const parsedValue = Number(storedValue);
    return Number.isFinite(parsedValue)
      ? clampSplitRatio(parsedValue)
      : DEFAULT_SPLIT_RATIO;
  });
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Sessão de colaboração ao vivo do DOCUMENTO (Yjs) — SEMPRE ativa p/ editores.
  // O painel de documento usa o editor colaborativo full-doc enquanto há provider.
  const [liveProvider, setLiveProvider] = useState<KindrawYjsProvider | null>(
    null,
  );
  const liveProviderRef = useRef<KindrawYjsProvider | null>(null);
  // Chave de cifra do canal de canvas (vem da collab room do drawing item).
  const [canvasRoomKey, setCanvasRoomKey] = useState<string | null>(null);
  const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI | null>(null);
  // state (além da ref) p/ o AppSidebar (menu de ícones/templates) reagir quando
  // a API do Excalidraw fica pronta.
  const [excalidrawAPI, setExcalidrawAPI] =
    useState<ExcalidrawImperativeAPI | null>(null);
  const splitBodyRef = useRef<HTMLDivElement | null>(null);
  const headerMenuRef = useRef<HTMLDivElement | null>(null);
  const isNarrow = useNarrowLayout();

  useEffect(() => {
    if (!headerMenuOpen) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (
        headerMenuRef.current &&
        !headerMenuRef.current.contains(event.target as Node)
      ) {
        setHeaderMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setHeaderMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [headerMenuOpen]);

  useEffect(() => {
    if (!unlinkConfirmOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setUnlinkConfirmOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [unlinkConfirmOpen]);

  const loadHybrid = useCallback(async () => {
    setErrorMessage(null);
    setStatusMessage(t("kindraw.hybrid.statusLoading"));

    try {
      const nextResponse = await getHybridItem(hybridId);
      const [docDraft, drawingDraft] = await Promise.all([
        getKindrawDraft(nextResponse.document.item.id),
        getKindrawDraft(nextResponse.drawing.item.id),
      ]);

      const restoredMarkdown =
        docDraft &&
        isDraftNewer(docDraft.updatedAt, nextResponse.document.item.updatedAt)
          ? docDraft.content
          : nextResponse.document.content;

      const restoredDrawing =
        drawingDraft &&
        isDraftNewer(
          drawingDraft.updatedAt,
          nextResponse.drawing.item.updatedAt,
        )
          ? drawingDraft.content
          : nextResponse.drawing.content;

      startTransition(() => {
        setResponse({
          ...nextResponse,
          document: {
            ...nextResponse.document,
            content: restoredMarkdown,
          },
          drawing: {
            ...nextResponse.drawing,
            content: restoredDrawing,
          },
        });
        setTitle(nextResponse.hybrid.title);
        setMarkdown(restoredMarkdown);
        setLastSavedMarkdown(nextResponse.document.content);
        setDrawingSeedContent(restoredDrawing);
        setSerializedDrawing(restoredDrawing);
        setLastSavedDrawing(nextResponse.drawing.content);
        setStatusMessage(t("kindraw.hybrid.statusSynced"));
      });
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, t("kindraw.hybrid.statusLoadFailed")),
      );
    }
  }, [hybridId, t]);

  useEffect(() => {
    void loadHybrid();
  }, [loadHybrid]);

  useEffect(() => {
    setActiveSectionId(initialSectionId);
  }, [initialSectionId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      `${HYBRID_SPLIT_STORAGE_PREFIX}${hybridId}`,
      String(splitRatio),
    );
  }, [hybridId, splitRatio]);

  useLayoutEffect(() => {
    if (initialView === "canvas") {
      setMobilePane("canvas");
      return;
    }

    setMobilePane("document");
  }, [initialView]);

  const persistMarkdown = useCallback(
    async (content: string) => {
      if (!response) {
        return;
      }

      const timestamp = new Date().toISOString();
      setSaveState("saving");
      await setKindrawDraft(response.document.item.id, {
        content,
        updatedAt: timestamp,
      });

      try {
        await updateItemContent(response.document.item.id, content);
        setLastSavedMarkdown(content);
        setSaveState("idle");
        setStatusMessage(t("kindraw.hybrid.statusDocumentSaved"));
        setResponse((current) =>
          current
            ? {
                ...current,
                document: {
                  ...current.document,
                  content,
                  item: {
                    ...current.document.item,
                    updatedAt: timestamp,
                  },
                },
              }
            : current,
        );
        await onTreeRefresh();
      } catch (error) {
        setSaveState("error");
        setStatusMessage(
          getErrorMessage(error, t("kindraw.hybrid.statusDocumentSaveFailed")),
        );
      }
    },
    [onTreeRefresh, response, t],
  );

  useEffect(() => {
    if (!response || markdown === lastSavedMarkdown) {
      return;
    }

    const timer = window.setTimeout(() => {
      void persistMarkdown(markdown);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [lastSavedMarkdown, markdown, persistMarkdown, response]);

  // Flush do documento no unmount/navegação: persiste a última edição pendente
  // (dentro da janela do debounce) p/ não perder ao sair. Refs p/ ler valores
  // atuais sem re-armar o effect (que só roda no unmount).
  const flushMarkdownRef = useRef<() => void>(() => undefined);
  flushMarkdownRef.current = () => {
    if (!response || markdown === lastSavedMarkdown) {
      return;
    }
    void persistMarkdown(markdown);
  };
  useEffect(
    () => () => {
      flushMarkdownRef.current();
    },
    [],
  );

  const persistDrawing = useCallback(
    async (content: string) => {
      if (!response) {
        return;
      }

      const timestamp = new Date().toISOString();
      setSaveState("saving");
      await setKindrawDraft(response.drawing.item.id, {
        content,
        updatedAt: timestamp,
      });

      try {
        await updateItemContent(response.drawing.item.id, content);
        setLastSavedDrawing(content);
        setSaveState("idle");
        setStatusMessage(t("kindraw.hybrid.statusCanvasSaved"));
        setResponse((current) =>
          current
            ? {
                ...current,
                drawing: {
                  ...current.drawing,
                  content,
                  item: {
                    ...current.drawing.item,
                    updatedAt: timestamp,
                  },
                },
              }
            : current,
        );
        await onTreeRefresh();
      } catch (error) {
        setSaveState("error");
        setStatusMessage(
          getErrorMessage(error, t("kindraw.hybrid.statusCanvasSaveFailed")),
        );
      }
    },
    [onTreeRefresh, response, t],
  );

  useEffect(() => {
    if (!response || !drawingSeedContent) {
      return;
    }

    if (!serializedDrawing || serializedDrawing === lastSavedDrawing) {
      return;
    }

    const timer = window.setTimeout(() => {
      void persistDrawing(serializedDrawing);
    }, 900);

    return () => window.clearTimeout(timer);
  }, [
    drawingSeedContent,
    lastSavedDrawing,
    persistDrawing,
    response,
    serializedDrawing,
  ]);

  // Flush do canvas no unmount/navegação: mesma garantia da do documento.
  const flushDrawingRef = useRef<() => void>(() => undefined);
  flushDrawingRef.current = () => {
    if (!response || !drawingSeedContent) {
      return;
    }
    if (!serializedDrawing || serializedDrawing === lastSavedDrawing) {
      return;
    }
    void persistDrawing(serializedDrawing);
  };
  useEffect(
    () => () => {
      flushDrawingRef.current();
    },
    [],
  );

  const commitTitle = useCallback(async () => {
    if (!response) {
      return;
    }

    const nextTitle = title.trim();
    if (!nextTitle) {
      setTitle(response.hybrid.title);
      return;
    }

    if (nextTitle === response.hybrid.title) {
      return;
    }

    try {
      await updateHybridItemMeta(hybridId, { title: nextTitle });
      setResponse((current) =>
        current
          ? {
              ...current,
              hybrid: {
                ...current.hybrid,
                title: nextTitle,
              },
              document: {
                ...current.document,
                item: {
                  ...current.document.item,
                  title: nextTitle,
                },
              },
              drawing: {
                ...current.drawing,
                item: {
                  ...current.drawing.item,
                  title: nextTitle,
                },
              },
            }
          : current,
      );
      setStatusMessage(t("kindraw.hybrid.statusTitleUpdated"));
      await onTreeRefresh();
    } catch (error) {
      setStatusMessage(
        getErrorMessage(error, t("kindraw.hybrid.statusTitleUpdateFailed")),
      );
      setTitle(response.hybrid.title);
    }
  }, [hybridId, onTreeRefresh, response, title, t]);

  const handleCreateShareLink = useCallback(
    async (access: "read" | "live-edit" = "read") => {
      try {
        const shareResponse = await createHybridShareLink(hybridId, access);

        setResponse((current) =>
          current
            ? {
                ...current,
                hybrid: {
                  ...current.hybrid,
                  shareLinks: [shareResponse.shareLink],
                },
              }
            : current,
        );
        setStatusMessage(
          access === "live-edit"
            ? t("kindraw.hybrid.statusLiveEditLinkCreated")
            : t("kindraw.status.publicLinkCreated"),
        );
        await onTreeRefresh();
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, t("kindraw.hybrid.statusPublicLinkCreateFailed")),
        );
      }
    },
    [hybridId, onTreeRefresh, t],
  );

  // SEMPRE-AO-VIVO: assim que o híbrido carrega e há um editor (usuário logado),
  // a sessão de colaboração do documento conecta sozinha — sem toggle "Iniciar".
  // Igual Eraser/Figma/tldraw: colaboração é implícita em ter acesso de edição.
  //
  // CRÍTICO: este effect só pode (re)criar o provider quando a IDENTIDADE DA
  // SESSÃO muda (híbrido, usuário, papel) — NUNCA a cada save. O `setResponse`
  // do autosave troca a referência de `response`; se ele estivesse nas deps, o
  // provider seria destruído/recriado a cada save, deixando o editor Tiptap preso
  // ao Y.Doc MORTO do provider anterior (o useEditor não reconfigura extensões),
  // e as edições parariam de persistir. Por isso dependemos só de primitivas
  // estáveis e lemos o resto via refs.
  const userId = currentUser?.id ?? null;
  // viewer (compartilhamento read-only) não entra em modo de escrita ao vivo.
  const canGoLive =
    Boolean(response) &&
    Boolean(currentUser) &&
    response?.hybrid.sharedRole !== "viewer";
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  useEffect(() => {
    if (!canGoLive) {
      return undefined;
    }
    const user = currentUserRef.current;
    if (!user) {
      return undefined;
    }

    const provider = new KindrawYjsProvider({
      roomId: `hdoc:${hybridId}`,
      user: {
        name: user.name || userHandle(user),
        color: colorForUser(user.id),
        avatarUrl: user.avatarUrl,
        githubLogin: user.githubLogin,
        userId: user.id,
      },
    });
    liveProviderRef.current = provider;
    setLiveProvider(provider);

    return () => {
      provider.destroy();
      liveProviderRef.current = null;
      setLiveProvider(null);
    };
    // Só recria quando a identidade da sessão muda — não a cada save (response).
  }, [canGoLive, hybridId, userId]);

  // Presença ao vivo (facepile + base p/ cursores) derivada do awareness.
  const presence = usePresence(liveProvider);

  // Garante a collab room do drawing (chave de cifra do canal de canvas).
  // Editores logados: habilita a room se ainda não houver e guarda a roomKey.
  useEffect(() => {
    if (!response || !currentUser) {
      return;
    }
    if (response.hybrid.sharedRole === "viewer") {
      return;
    }
    const existing = response.drawing.collaborationRoom?.roomKey;
    if (existing) {
      setCanvasRoomKey(existing);
      return;
    }
    let cancelled = false;
    void enableCollaborationRoom(response.drawing.item.id)
      .then((res) => {
        if (!cancelled) {
          setCanvasRoomKey(res.collaborationRoom.roomKey);
        }
      })
      .catch(() => {
        /* canvas ao vivo é best-effort; doc segue ao vivo de qualquer forma */
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser, response]);

  // Cliente de colaboração de canvas (enxuto). Conecta ao canal hcanvas:<id>
  // com a roomKey do drawing; renderiza cursores/seleção nativamente.
  const canvasCollab = useCanvasCollab({
    enabled: Boolean(currentUser) && Boolean(canvasRoomKey),
    roomId: `hcanvas:${hybridId}`,
    roomKey: canvasRoomKey,
    profile: {
      name:
        currentUser?.name ||
        (currentUser ? userHandle(currentUser) : "") ||
        t("kindraw.hybrid.youLabel"),
      userId: currentUser?.id ?? null,
      avatarUrl: currentUser?.avatarUrl ?? null,
      githubLogin: currentUser?.githubLogin ?? null,
    },
    excalidrawAPIRef,
  });

  // Repassa os colaboradores do canvas (cursores/seleção) p/ o Excalidraw, que
  // os renderiza nativamente.
  useEffect(() => {
    excalidrawAPIRef.current?.updateScene({
      collaborators: canvasCollab.collaborators,
    });
  }, [canvasCollab.collaborators]);

  // Idle/grace: marca o usuário como ausente após inatividade (dim no facepile,
  // base p/ fade do cursor antes da remoção). Atividade volta a "ativo".
  useEffect(() => {
    if (!liveProvider) {
      return undefined;
    }
    let idleTimer: number | null = null;
    const IDLE_MS = 60_000;
    const goActive = () => {
      liveProvider.setIdle(false);
      if (idleTimer !== null) {
        window.clearTimeout(idleTimer);
      }
      idleTimer = window.setTimeout(() => liveProvider.setIdle(true), IDLE_MS);
    };
    const onHidden = () => {
      if (document.visibilityState === "hidden") {
        liveProvider.setIdle(true);
      } else {
        goActive();
      }
    };
    goActive();
    window.addEventListener("pointermove", goActive, { passive: true });
    window.addEventListener("keydown", goActive);
    document.addEventListener("visibilitychange", onHidden);
    return () => {
      if (idleTimer !== null) {
        window.clearTimeout(idleTimer);
      }
      window.removeEventListener("pointermove", goActive);
      window.removeEventListener("keydown", goActive);
      document.removeEventListener("visibilitychange", onHidden);
    };
  }, [liveProvider]);

  const handleRevokeShareLink = useCallback(
    async (shareLinkId: string) => {
      try {
        await revokeShareLink(shareLinkId);
        setResponse((current) =>
          current
            ? {
                ...current,
                hybrid: {
                  ...current.hybrid,
                  shareLinks: [],
                },
              }
            : current,
        );
        await onTreeRefresh();
      } catch (error) {
        setStatusMessage(
          getErrorMessage(error, t("kindraw.hybrid.statusPublicLinkRevokeFailed")),
        );
      }
    },
    [onTreeRefresh, t],
  );

  const handleUnlink = useCallback(async () => {
    if (!response) {
      return;
    }

    try {
      await deleteHybridItem(hybridId);
      await onTreeRefresh();
      navigateKindraw(buildItemPath(response.document.item));
    } catch (error) {
      setStatusMessage(
        getErrorMessage(error, t("kindraw.hybrid.statusUnlinkFailed")),
      );
    }
  }, [hybridId, onTreeRefresh, response, t]);

  const setView = useCallback(
    async (view: KindrawHybridView, sectionId?: string | null) => {
      navigateKindraw(
        buildHybridPath(hybridId, {
          view,
          sectionId: sectionId ?? activeSectionId,
        }),
      );

      if (response && view !== response.hybrid.defaultView) {
        try {
          await updateHybridItemMeta(hybridId, { defaultView: view });
          setResponse((current) =>
            current
              ? {
                  ...current,
                  hybrid: {
                    ...current.hybrid,
                    defaultView: view,
                  },
                }
              : current,
          );
        } catch (error) {
          console.warn("Failed to persist hybrid default view", error);
        }
      }
    },
    [activeSectionId, hybridId, response],
  );

  /**
   * Aplica o link kindraw aos elementos cujos ids estão em `selectedIds`.
   * Retorna `true` se ao menos um elemento foi vinculado.
   */
  const linkElementsToSection = useCallback(
    (sectionId: string, selectedIds: string[]) => {
      if (!sceneElements.length || !sceneAppState) {
        setStatusMessage(t("kindraw.hybrid.statusCanvasNotReady"));
        return false;
      }

      if (!selectedIds.length) {
        setStatusMessage(t("kindraw.hybrid.statusSelectAtLeastOne"));
        return false;
      }

      const selectedSet = new Set(selectedIds);
      const nextElements = sceneElements.map((element) =>
        selectedSet.has(element.id)
          ? newElementWith(element, {
              link: buildKindrawSectionLink(hybridId, sectionId),
            })
          : element,
      );

      excalidrawAPIRef.current?.updateScene({
        elements: nextElements,
      });
      setSceneElements(nextElements);
      setSerializedDrawing(
        serializeAsJSON(nextElements, sceneAppState, sceneFiles, "local"),
      );
      return true;
    },
    [hybridId, sceneAppState, sceneElements, sceneFiles, t],
  );

  /**
   * Ação "Vincular" da seção. Se já há seleção no canvas, vincula na hora;
   * caso contrário entra no "modo vincular" e espera a próxima seleção.
   */
  const handleLinkSelection = useCallback(
    (sectionId: string) => {
      // Toggle: clicar de novo na seção que já está aguardando cancela o modo.
      if (linkingSectionId === sectionId) {
        setLinkingSectionId(null);
        return;
      }

      const selectedIds = Object.keys(selectedElementIds);
      if (selectedIds.length > 0) {
        if (linkElementsToSection(sectionId, selectedIds)) {
          setLinkingSectionId(null);
          setStatusMessage(t("kindraw.hybrid.statusSelectionLinked"));
        }
        return;
      }

      // Sem seleção: entra no modo vincular e garante canvas visível.
      setLinkingSectionId(sectionId);
      setActiveSectionId(sectionId);
      void setView("both", sectionId);
      setStatusMessage(t("kindraw.hybrid.statusSelectToLink"));
    },
    [linkElementsToSection, linkingSectionId, selectedElementIds, setView, t],
  );

  const handleAddSection = useCallback(() => {
    const { markdown: nextMarkdown, sectionId } = appendHybridSection(
      markdown,
      t("kindraw.hybrid.newSectionTitle"),
    );
    setMarkdown(nextMarkdown);
    setActiveSectionId(sectionId);
    setStatusMessage(t("kindraw.hybrid.statusSectionCreated"));
    return sectionId;
  }, [markdown, t]);

  const handleFocusSectionOnCanvas = useCallback(
    (sectionId: string) => {
      setActiveSectionId(sectionId);
      void setView("both", sectionId);

      const linked = sceneElements.filter((element) => {
        if (!element.link) {
          return false;
        }
        const target = parseKindrawSectionLink(element.link);
        return target?.hybridId === hybridId && target.sectionId === sectionId;
      });

      if (!linked.length) {
        return;
      }

      const linkedIds = linked.map((element) => element.id);

      // O setView pode trocar o layout (mostrar o canvas) e remontar o canvas
      // antes do zoom rodar; damos um tempo para a API estar pronta e usamos os
      // elementos ATUAIS da cena (por id) para garantir referências válidas.
      window.setTimeout(() => {
        const api = excalidrawAPIRef.current;
        if (!api) {
          return;
        }
        const targets = api
          .getSceneElements()
          .filter((element: ExcalidrawElement) =>
            linkedIds.includes(element.id),
          );
        if (!targets.length) {
          return;
        }
        api.updateScene({
          appState: {
            selectedElementIds: Object.fromEntries(
              targets.map((element: ExcalidrawElement) => [element.id, true]),
            ),
          },
        });
        // o zoom precisa rodar depois da seleção assentar, senão o
        // scrollToContent é engolido pelo updateScene do mesmo tick.
        window.setTimeout(() => {
          api.scrollToContent(targets, {
            fitToContent: true,
            animate: true,
            duration: 500,
          });
        }, 60);
      }, 80);
    },
    [hybridId, sceneElements, setView],
  );

  // Ponte para as menções "@" de frame no documento: lista os frames do canvas
  // e dá foco (zoom + seleção) num frame ao clicar na menção. Objeto estável
  // (lê excalidrawAPIRef no momento da chamada), então o editor pode capturá-lo
  // uma vez sem ficar stale.
  const frameMention = useMemo(
    () => ({
      getFrames: (): FrameRef[] => {
        const api = excalidrawAPIRef.current;
        if (!api) {
          return [];
        }
        let counter = 0;
        return api
          .getSceneElements()
          .filter(
            (element: ExcalidrawElement) =>
              element.type === "frame" && !element.isDeleted,
          )
          .map((element: ExcalidrawElement) => {
            counter += 1;
            const rawName =
              "name" in element && typeof element.name === "string"
                ? element.name.trim()
                : "";
            return { id: element.id, name: rawName || `Frame ${counter}` };
          });
      },
      focusFrame: (id: string) => {
        if (!excalidrawAPIRef.current) {
          return;
        }
        // Garante o canvas visível (o layout pode estar em "doc").
        void setView("both");
        window.setTimeout(() => {
          const api = excalidrawAPIRef.current;
          if (!api) {
            return;
          }
          const target = api
            .getSceneElements()
            .find((element: ExcalidrawElement) => element.id === id);
          if (!target) {
            return;
          }
          api.updateScene({
            appState: { selectedElementIds: { [id]: true } },
          });
          window.setTimeout(() => {
            api.scrollToContent(target, {
              fitToContent: true,
              animate: true,
              duration: 500,
            });
          }, 60);
        }, 80);
      },
    }),
    [setView],
  );

  // Esc cancela o modo vincular.
  useEffect(() => {
    if (!linkingSectionId) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLinkingSectionId(null);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [linkingSectionId]);

  const handleCanvasLinkOpen = useCallback<
    NonNullable<ExcalidrawProps["onLinkOpen"]>
  >(
    (element, event) => {
      if (!element.link) {
        return;
      }

      const target = parseKindrawSectionLink(element.link);
      if (!target || target.hybridId !== hybridId) {
        return;
      }

      event.preventDefault();
      setActiveSectionId(target.sectionId);
      void setView("both", target.sectionId);
    },
    [hybridId, setView],
  );

  const handleSplitPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const body = splitBodyRef.current;
      if (!body) {
        return;
      }

      event.preventDefault();
      const pointerId = event.pointerId;
      event.currentTarget.setPointerCapture(pointerId);

      const updateFromClientX = (clientX: number) => {
        const bounds = body.getBoundingClientRect();
        if (!bounds.width) {
          return;
        }

        setSplitRatio(clampSplitRatio((clientX - bounds.left) / bounds.width));
      };

      updateFromClientX(event.clientX);

      const onPointerMove = (moveEvent: PointerEvent) => {
        updateFromClientX(moveEvent.clientX);
      };

      const onPointerUp = () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp, { once: true });
    },
    [],
  );

  const hybridItemsById = useMemo(
    () =>
      response
        ? {
            ...itemsById,
            [response.document.item.id]: response.document.item,
            [response.drawing.item.id]: response.drawing.item,
          }
        : itemsById,
    [itemsById, response],
  );

  const drawingInitialData = useMemo<ExcalidrawInitialDataState | null>(
    () => (drawingSeedContent ? parseDrawingContent(drawingSeedContent) : null),
    [drawingSeedContent],
  );

  const sections = useMemo(
    () => parseHybridMarkdownSections(markdown),
    [markdown],
  );

  /** Seções com pelo menos um elemento do canvas vinculado (element.link). */
  const linkedSectionIds = useMemo(() => {
    const ids = new Set<string>();
    for (const element of sceneElements) {
      if (!element.link) {
        continue;
      }

      const target = parseKindrawSectionLink(element.link);
      if (target && target.hybridId === hybridId) {
        ids.add(target.sectionId);
      }
    }
    return ids;
  }, [hybridId, sceneElements]);

  const linkingSection = useMemo(
    () =>
      linkingSectionId
        ? sections.find((section) => section.id === linkingSectionId) || null
        : null,
    [linkingSectionId, sections],
  );

  // Modo vincular: assim que o usuário seleciona ≥1 elemento no canvas, vincula
  // automaticamente à seção aguardando. Como só entramos no modo quando NÃO havia
  // seleção (ver handleLinkSelection), qualquer seleção observada aqui é nova.
  useEffect(() => {
    if (!linkingSectionId) {
      return;
    }

    const selectedIds = Object.keys(selectedElementIds);
    if (!selectedIds.length) {
      return;
    }

    const sectionTitle =
      linkingSection?.title || t("kindraw.hybrid.sectionFallback");
    if (linkElementsToSection(linkingSectionId, selectedIds)) {
      setLinkingSectionId(null);
      setStatusMessage(
        t("kindraw.hybrid.statusSelectionLinkedTo", { section: sectionTitle }),
      );
    }
  }, [
    linkElementsToSection,
    linkingSection,
    linkingSectionId,
    selectedElementIds,
    t,
  ]);

  if (errorMessage) {
    return (
      <div className="kindraw-empty-state">
        <h2>{t("kindraw.hybrid.unavailableTitle")}</h2>
        <p>{errorMessage}</p>
      </div>
    );
  }

  if (!response || !drawingInitialData) {
    return (
      <div className="kindraw-loading-shell">
        <p>{statusMessage}</p>
      </div>
    );
  }

  const showDocument = initialView === "document" || initialView === "both";
  const showCanvas = initialView === "canvas" || initialView === "both";
  const folderName =
    folders?.find((folder) => folder.id === response.hybrid.folderId)?.name ||
    t("kindraw.sidebar.backToLibrary");

  const documentPane = (
    <section className="kindraw-hybrid-shell__document">
      <HybridMarkdownPane
        activeSectionId={activeSectionId}
        canLinkSelection={showCanvas}
        collabProvider={liveProvider}
        canSeed={canGoLive}
        collabUser={
          currentUser
            ? {
                name: currentUser.name || userHandle(currentUser),
                color: colorForUser(currentUser.id),
                avatarUrl: currentUser.avatarUrl,
                githubLogin: currentUser.githubLogin,
                userId: currentUser.id,
              }
            : null
        }
        hybridId={hybridId}
        itemsById={hybridItemsById}
        linkedSectionIds={linkedSectionIds}
        linkingSectionId={linkingSectionId}
        markdown={markdown}
        onAddSection={handleAddSection}
        onFocusSectionOnCanvas={handleFocusSectionOnCanvas}
        onLinkSelection={handleLinkSelection}
        onMarkdownChange={setMarkdown}
        onNavigate={navigateKindraw}
        onOpenCanvas={(sectionId) => {
          setActiveSectionId(sectionId);
          void setView("canvas", sectionId);
        }}
        onStatusMessage={setStatusMessage}
        frameMention={frameMention}
      />
    </section>
  );
  const canvasPane = (
    <section className="kindraw-hybrid-shell__canvas">
      {linkingSection ? (
        <div className="kindraw-linkbar" role="status">
          <span className="kindraw-linkbar__text">
            <KindrawIcon name="link" size={13} />{" "}
            {t("kindraw.hybrid.linkbarInstruction", {
              title: LINKBAR_TITLE_SENTINEL,
            })
              .split(LINKBAR_TITLE_SENTINEL)
              .flatMap((segment, index) =>
                index === 0
                  ? [segment]
                  : [
                      <strong key="title">{linkingSection.title}</strong>,
                      segment,
                    ],
              )}
          </span>
          <button
            className="kindraw-linkbar__cancel"
            onClick={() => setLinkingSectionId(null)}
            type="button"
          >
            {t("kindraw.hybrid.cancel")}
          </button>
        </div>
      ) : null}
      <Excalidraw
        key={response.drawing.item.id}
        onExcalidrawAPI={(api) => {
          excalidrawAPIRef.current = api;
          setExcalidrawAPI(api);
        }}
        initialData={drawingInitialData}
        isCollaborating={canvasCollab.isConnected}
        onChange={(elements, appState, files) => {
          setSceneElements(elements);
          setSceneAppState(appState);
          setSceneFiles(files);
          setSelectedElementIds(appState.selectedElementIds);
          setSerializedDrawing(
            serializeAsJSON(elements, appState, files, "local"),
          );
          // propaga a cena aos outros participantes do canvas (no-op se eco).
          canvasCollab.broadcastScene(elements);
        }}
        onPointerUpdate={canvasCollab.onPointerUpdate}
        onLinkOpen={handleCanvasLinkOpen}
        renderTopRightUI={(isMobile) =>
          isMobile ? null : (
            <button
              aria-label={t("kindraw.hybrid.insertIconsTemplates")}
              className="kindraw-insert-trigger"
              onClick={() =>
                excalidrawAPIRef.current?.toggleSidebar({ name: "kindraw" })
              }
              title={t("kindraw.hybrid.insertIconsTemplates")}
              type="button"
            >
              {LibraryIcon}
            </button>
          )
        }
      >
        {/* Menu "Inserir" (ícones + templates/fluxogramas) — mesmo do editor de
            canvas. Desacoplado: só usa excalidrawAPI + route. */}
        <AppSidebar
          excalidrawAPI={excalidrawAPI}
          route={{
            kind: "hybrid",
            hybridId,
            view: initialView,
            sectionId: activeSectionId,
          }}
        />
        {/* AI no canvas do hybrid: Text-to-diagram (menu Generate) +
            diagram-to-code (magic frame). Mesmo wiring do editor /draw. */}
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}
      </Excalidraw>
    </section>
  );

  return (
    <div className="kindraw-editor-shell kindraw-hybrid-shell">
      <header className="kindraw-editor-header">
        <div className="kindraw-editor-header__leading">
          <button
            aria-label={t("kindraw.hybrid.backToFolder")}
            className="kindraw-iconbtn"
            onClick={() =>
              navigateKindraw(buildFolderPath(response.hybrid.folderId))
            }
            type="button"
          >
            <KindrawIcon name="back" size={17} />
          </button>
          <span className="kindraw-editor-crumb">{folderName} /</span>
          <div className="kindraw-editor-title">
            <input
              aria-label={t("kindraw.hybrid.titleInputAria")}
              className="kindraw-editor-title__input"
              onBlur={() => void commitTitle()}
              onChange={(event) => setTitle(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }
              }}
              size={Math.min(Math.max(title.length, 4), 48)}
              type="text"
              value={title}
            />
            <KindrawIcon name="pen" size={13} />
          </div>
        </div>
        <div className="kindraw-segment kindraw-editor-header__segment">
          {getHybridViewLabels(t).map(({ view, label }) => (
            <button
              className={`kindraw-segment__btn${
                initialView === view ? " kindraw-segment__btn--active" : ""
              }`}
              key={view}
              onClick={() => void setView(view)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        <div className="kindraw-editor-header__trailing">
          <div className="kindraw-header-collab">
            <PresenceFacepile users={presence} />
            <span
              className={`kindraw-pill kindraw-pill--${
                saveState === "idle" ? "ok" : saveState
              }`}
              title={statusMessage}
            >
              <i />
              <span>{statusMessage}</span>
            </span>
          </div>
          <div className="kindraw-header-actions">
          <button
            className="kindraw-btn kindraw-btn--primary"
            onClick={() => setShareModalOpen(true)}
            type="button"
          >
            <KindrawIcon name="share" size={16} /> {t("kindraw.hybrid.share")}
          </button>
          <div className="kindraw-menuwrap" ref={headerMenuRef}>
            <button
              aria-expanded={headerMenuOpen}
              aria-label={t("kindraw.hybrid.moreActions")}
              className="kindraw-dots kindraw-dots--visible"
              onClick={() => setHeaderMenuOpen(!headerMenuOpen)}
              type="button"
            >
              <KindrawIcon name="dots" size={16} />
            </button>
            {headerMenuOpen ? (
              <div className="kindraw-popover kindraw-popover--menu">
                <div className="kindraw-menu" role="menu">
                  <button
                    className="kindraw-menu__item kindraw-menu__item--danger"
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      setUnlinkConfirmOpen(true);
                    }}
                    role="menuitem"
                    type="button"
                  >
                    {t("kindraw.hybrid.unlink")}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          </div>
        </div>
      </header>

      {initialView === "both" && isNarrow ? (
        <>
          <div className="kindraw-segment kindraw-segment--tabs">
            <button
              className={`kindraw-segment__btn${
                mobilePane === "document" ? " kindraw-segment__btn--active" : ""
              }`}
              onClick={() => setMobilePane("document")}
              type="button"
            >
              {t("kindraw.hybrid.viewDocument")}
            </button>
            <button
              className={`kindraw-segment__btn${
                mobilePane === "canvas" ? " kindraw-segment__btn--active" : ""
              }`}
              onClick={() => setMobilePane("canvas")}
              type="button"
            >
              {t("kindraw.actions.currentCanvas")}
            </button>
          </div>
          <div className="kindraw-hybrid-shell__body">
            {mobilePane === "document" ? documentPane : canvasPane}
          </div>
        </>
      ) : (
        <div
          className={`kindraw-hybrid-shell__body${
            initialView === "both" ? " kindraw-hybrid-shell__body--both" : ""
          }`}
          ref={splitBodyRef}
          style={
            initialView === "both"
              ? {
                  gridTemplateColumns: `${splitRatio}fr 14px ${
                    1 - splitRatio
                  }fr`,
                }
              : undefined
          }
        >
          {showDocument ? documentPane : null}
          {initialView === "both" ? (
            <button
              aria-label={t("kindraw.hybrid.resizeSplitAria")}
              className="kindraw-hybrid-shell__divider"
              onPointerDown={handleSplitPointerDown}
              type="button"
            >
              <i />
              <i />
              <i />
            </button>
          ) : null}
          {showCanvas ? canvasPane : null}
        </div>
      )}

      {unlinkConfirmOpen ? (
        <div
          className="kindraw-modal-overlay"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setUnlinkConfirmOpen(false);
            }
          }}
        >
          <div aria-modal="true" className="kindraw-modal" role="dialog">
            <h2>{t("kindraw.hybrid.unlinkModalTitle")}</h2>
            <p>{t("kindraw.hybrid.unlinkModalDescription")}</p>
            <div className="kindraw-modal__actions">
              <button
                className="kindraw-btn kindraw-btn--soft"
                onClick={() => setUnlinkConfirmOpen(false)}
                type="button"
              >
                {t("kindraw.hybrid.cancel")}
              </button>
              <button
                className="kindraw-btn kindraw-btn--danger"
                onClick={() => {
                  setUnlinkConfirmOpen(false);
                  void handleUnlink();
                }}
                type="button"
              >
                {t("kindraw.hybrid.unlink")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {shareModalOpen ? (
        <ShareHybridModal
          buildShareUrl={(token) => buildPublicShareUrl(token, { view: "both" })}
          busy={saveState === "saving"}
          hybrid={{ id: hybridId, title: response.hybrid.title }}
          onChange={() => void onTreeRefresh()}
          onClose={() => setShareModalOpen(false)}
          onCreateShareLink={handleCreateShareLink}
          onRevokeShareLink={handleRevokeShareLink}
          shareLinks={response.hybrid.shareLinks}
        />
      ) : null}
    </div>
  );
};
